import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormMethods } from 'react-hook-form';
import { Col, Row } from 'react-flexbox-grid';
import { FaInfoCircle } from 'react-icons/fa';
import FormItem from 'common/components/formItem/FormItem';
import Label from 'common/components/label/Label';
import SelectController from 'common/components/select/SelectController';
import Modal from 'common/components/modal/Modal';
import Button from 'common/components/button/Button';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { CompanyDto } from 'api/companies/models/CompanyDto';
import { getWidgetsHiddenInGroup } from 'screens/dashboard/widgetRegistry';
import styles from '../CompanyScreen.module.scss';

type Props = {
    form: UseFormMethods<CompanyDto>;
    isDisabled: boolean;
};

// Campo (platform admin) que define o grupo de dashboards da empresa: FULL vê tudo, RGPD esconde
// os widgets identificáveis por condutor. O modal de comparação lê o registry, por isso reflete
// automaticamente qualquer widget que venha a ser restringido no grupo RGPD no futuro.
const DashboardGroupField = ({ form, isDisabled }: Props) => {
    const { t } = useTranslation();
    const [showInfo, setShowInfo] = useState(false);
    const watchedGroup = form.watch('dashboardGroup') || 'FULL';

    const options: SelectValueLabel[] = [
        { value: 'FULL', label: t('companies.dashboard_group.full') as string },
        { value: 'RGPD', label: t('companies.dashboard_group.rgpd') as string },
    ];

    const hiddenWidgets = getWidgetsHiddenInGroup('RGPD');

    return (
        <Row>
            <Col xs={12} xl={4}>
                <FormItem>
                    <Label className={styles.label}>
                        {t('companies.dashboard_group.label') as string}
                        <FaInfoCircle
                            style={{ marginLeft: 6, cursor: 'pointer' }}
                            onClick={() => setShowInfo(true)}
                        />
                    </Label>
                    <SelectController
                        form={form}
                        name="dashboardGroup"
                        options={options}
                        placeholder={t('common.select_option') as string}
                        rules={{ required: false }}
                        isDisabled={isDisabled}
                    />
                </FormItem>
            </Col>
            <Col xs={12} xl={8}>
                <div className={styles.reservationTypeInfo}>
                    <FaInfoCircle className={styles.reservationTypeInfoIcon} />
                    <span>
                        {watchedGroup === 'RGPD'
                            ? t('companies.dashboard_group.rgpd_desc') as string
                            : t('companies.dashboard_group.full_desc') as string}
                    </span>
                </div>
            </Col>

            <Modal
                isOpen={showInfo}
                onRequestClose={() => setShowInfo(false)}
                style={{ content: { width: 600, maxWidth: '90vw', minWidth: 'unset' } }}
            >
                <div style={{ padding: 24 }}>
                    <h3 style={{ marginTop: 0 }}>{t('companies.dashboard_group.compare_title') as string}</h3>
                    <p>{t('companies.dashboard_group.compare_intro') as string}</p>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                        {t('companies.dashboard_group.rgpd_hidden_title') as string}
                    </p>
                    <ul style={{ marginTop: 0 }}>
                        {hiddenWidgets.map(w => (
                            <li key={w.id}>{t(w.titleKey as any) as string}</li>
                        ))}
                    </ul>
                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                        {t('companies.dashboard_group.rgpd_alternative_title') as string}
                    </p>
                    <p>{t('companies.dashboard_group.rgpd_alternative_desc') as string}</p>
                    <div style={{ textAlign: 'right', marginTop: 16 }}>
                        <Button text={t('common.close') as string} onClick={() => setShowInfo(false)} />
                    </div>
                </div>
            </Modal>
        </Row>
    );
};

export default DashboardGroupField;
